import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { NormalizedOrder, OrdersResponse } from '../../types/unwinding.types';

interface OrdersPanelProps {
  data: OrdersResponse | null | undefined;
  isLoading?: boolean;
}

const timeAgo = (dateStr: string): string => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
};

const fmtPrice = (v: number | null | undefined): string => {
  if (v == null || isNaN(v)) return '\u2014';
  if (v === 0) return '0.00';
  if (v >= 0.1) return v.toFixed(2);
  if (v >= 0.01) return v.toFixed(3);
  return v.toFixed(4);
};

const headerSx = {
  fontSize: '0.58rem',
  fontWeight: 600,
  color: 'grey.500',
  py: 0.2,
  px: 0.2,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

const cellSx = { fontSize: '0.72rem', py: 0.15, px: 0.15 } as const;

const statusColorMap: Record<string, 'default' | 'info' | 'warning'> = {
  resting: 'default',
  live: 'info',
  partial: 'warning',
};

export const OrdersPanel = ({ data, isLoading }: OrdersPanelProps) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <Typography sx={{ color: 'grey.500', fontSize: '0.8rem' }}>Loading orders...</Typography>
      </Box>
    );
  }

  const orders = data?.orders ?? [];

  return (
    <Box>
      {/* Summary bar */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.5 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'white' }}>Orders</Typography>
        <Chip
          icon={
            <img
              src="/assets/exchanges/png/100x100/kalshi.png"
              style={{ width: 14, height: 14, borderRadius: '50%' }}
            />
          }
          label={`${data?.summary.kalshi.count ?? 0} orders \u00B7 $${(data?.summary.kalshi.totalVolume ?? 0).toFixed(0)}`}
          size="small"
          sx={{ fontSize: '0.65rem' }}
        />
        <Chip
          icon={
            <img
              src="/assets/exchanges/png/100x100/polymarket.png"
              style={{ width: 14, height: 14, borderRadius: '50%' }}
            />
          }
          label={`${data?.summary.polymarket.count ?? 0} orders \u00B7 $${(data?.summary.polymarket.totalVolume ?? 0).toFixed(0)}`}
          size="small"
          sx={{ fontSize: '0.65rem' }}
        />
      </Stack>

      {orders.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography sx={{ color: 'grey.500', fontSize: '0.85rem' }}>No open orders</Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerSx, width: 30 }}>Plat</TableCell>
                <TableCell sx={{ ...headerSx, width: 120 }}>Market</TableCell>
                <TableCell sx={{ ...headerSx, width: 35 }} align="center">Side</TableCell>
                <TableCell sx={{ ...headerSx, width: 35 }} align="center">Action</TableCell>
                <TableCell sx={{ ...headerSx, width: 45 }} align="right">Price</TableCell>
                <TableCell sx={{ ...headerSx, width: 50 }} align="right">Qty</TableCell>
                <TableCell sx={{ ...headerSx, width: 40 }} align="right">Filled</TableCell>
                <TableCell sx={{ ...headerSx, width: 55 }} align="center">Status</TableCell>
                <TableCell sx={{ ...headerSx, width: 50 }} align="center">Source</TableCell>
                <TableCell sx={{ ...headerSx, width: 55 }} align="right">Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order: NormalizedOrder) => (
                <OrderRow key={order.id} order={order} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const OrderRow = ({ order }: { order: NormalizedOrder }) => {
  const platformImg =
    order.platform === 'kalshi'
      ? '/assets/exchanges/png/100x100/kalshi.png'
      : '/assets/exchanges/png/100x100/polymarket.png';

  const statusColor = statusColorMap[order.status] ?? 'default';

  return (
    <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
      {/* Platform */}
      <TableCell sx={cellSx}>
        <img src={platformImg} alt={order.platform} style={{ width: 14, height: 14, borderRadius: '50%' }} />
      </TableCell>

      {/* Market */}
      <TableCell sx={{ ...cellSx, overflow: 'hidden' }}>
        <Tooltip title={order.marketId} placement="top">
          <Typography
            sx={{
              fontSize: '0.65rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {order.marketId}
          </Typography>
        </Tooltip>
      </TableCell>

      {/* Side */}
      <TableCell sx={cellSx} align="center">
        <Chip
          label={order.side.toUpperCase()}
          size="small"
          color={order.side === 'yes' ? 'success' : 'error'}
          variant="outlined"
          sx={{ fontSize: '0.5rem', height: 16, '& .MuiChip-label': { px: 0.4 } }}
        />
      </TableCell>

      {/* Action */}
      <TableCell sx={cellSx} align="center">
        <Typography
          sx={{
            fontSize: '0.6rem',
            fontWeight: 600,
            color: order.action === 'buy' ? 'success.light' : 'error.light',
          }}
        >
          {order.action.toUpperCase()}
        </Typography>
      </TableCell>

      {/* Price */}
      <TableCell sx={cellSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(order.price)}</Typography>
      </TableCell>

      {/* Qty: remaining / total */}
      <TableCell sx={cellSx} align="right">
        <Typography sx={{ fontSize: '0.65rem' }}>
          {order.remainingQuantity} / {order.quantity}
        </Typography>
      </TableCell>

      {/* Filled */}
      <TableCell sx={cellSx} align="right">
        <Typography sx={{ fontSize: '0.65rem', color: order.filledQuantity > 0 ? 'warning.light' : 'grey.600' }}>
          {order.filledQuantity}
        </Typography>
      </TableCell>

      {/* Status */}
      <TableCell sx={cellSx} align="center">
        <Chip
          label={order.status}
          size="small"
          color={statusColor}
          variant="outlined"
          sx={{ fontSize: '0.5rem', height: 16, '& .MuiChip-label': { px: 0.4 } }}
        />
      </TableCell>

      {/* Source */}
      <TableCell sx={cellSx} align="center">
        {order.isUnwinding ? (
          <Chip
            label="unwind"
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: '0.5rem', height: 16, '& .MuiChip-label': { px: 0.4 } }}
          />
        ) : null}
      </TableCell>

      {/* Updated */}
      <TableCell sx={cellSx} align="right">
        <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{timeAgo(order.updatedAt)}</Typography>
      </TableCell>
    </TableRow>
  );
};
